import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactSection = () => {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast.error(language === 'ta' ? 'அனைத்து புலங்களையும் நிரப்பவும்' : 'Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...formData, language });
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'form_submission', form_name: 'contact_form', form_department: 'General', form_source: 'homepage_contact' });
      }
      if (window.gtag) { window.gtag('event', 'conversion', { send_to: 'AW-17542370694/contact_form' }); }
      toast.success(language === 'ta' ? 'செய்தி அனுப்பப்பட்டது!' : 'Message sent!');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch {
      toast.error(language === 'ta' ? 'பிழை ஏற்பட்டது' : 'Error sending message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-14 bg-white" data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1.5 bg-[#0066b3]/8 text-[#0066b3] rounded-full text-xs font-semibold uppercase tracking-wider mb-3">
            {language === 'ta' ? 'தொடர்பு' : 'Contact'}
          </span>
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0f172a] tracking-tight">
            {t('contact.title')}
          </h2>
        </div>

        {/* Info strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { icon: MapPin, label: language === 'ta' ? 'முகவரி' : 'Address', value: '175, Big Street, Triplicane', color: '#0066b3' },
            { icon: Phone, label: language === 'ta' ? 'தொலைபேசி' : 'Phone', value: '+91 44 2844 2626', color: '#c41e3a' },
            { icon: Mail, label: language === 'ta' ? 'மின்னஞ்சல்' : 'Email', value: 'info@sakthihospitals.com', color: '#00897b' },
            { icon: Clock, label: language === 'ta' ? 'நேரம்' : 'Hours', value: 'OPD: 9AM-9PM | ER: 24/7', color: '#f57c00' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3 bg-[#f8fafc] rounded-xl p-3 border border-gray-100" data-testid={`contact-info-${item.label.toLowerCase()}`}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${item.color}12`, color: item.color }}>
                <item.icon size={16} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] text-[#94a3b8] uppercase font-semibold">{item.label}</div>
                <div className="text-xs font-medium text-[#1a2b3c] truncate">{item.value}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Map - 3 cols */}
          <div className="lg:col-span-3 rounded-xl overflow-hidden shadow-sm border border-gray-100 h-[320px]" data-testid="google-maps-embed">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.0!2d80.2757397!3d13.0570045!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a526605d0a030d5%3A0x6e079bd99529d311!2sSakthi%20Hospital%20%7C%20Best%20Hospital%20in%20Triplicane%20and%20Royapettah!5e0!3m2!1sen!2sin!4v1704067200000!5m2!1sen!2sin"
              width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy"
              title="Sakthi Hospital - Triplicane, Chennai"
            ></iframe>
          </div>

          {/* Contact form - 2 cols */}
          <div className="lg:col-span-2 bg-[#f8fafc] rounded-xl p-5 border border-gray-100" data-testid="contact-form">
            <h3 className="text-sm font-bold text-[#0f172a] mb-4">
              {language === 'ta' ? 'செய்தி அனுப்புங்கள்' : 'Send a Message'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input type="hidden" id="zc_gad" name="zc_gad" value="" />
              <Input
                placeholder={language === 'ta' ? 'பெயர்' : 'Your Name'}
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="h-10 text-sm border-gray-200" required data-testid="contact-name-input"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="email" placeholder={language === 'ta' ? 'மின்னஞ்சல்' : 'Email'}
                  value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="h-10 text-sm border-gray-200" required data-testid="contact-email-input"
                />
                <Input
                  type="tel" placeholder={language === 'ta' ? 'தொலைபேசி' : 'Phone'}
                  value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="h-10 text-sm border-gray-200" required data-testid="contact-phone-input"
                />
              </div>
              <Textarea
                placeholder={language === 'ta' ? 'செய்தி' : 'Your Message'}
                value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="text-sm border-gray-200 min-h-[80px] resize-none" required data-testid="contact-message-input"
              />
              <Button type="submit" disabled={loading} className="w-full h-10 bg-[#0066b3] hover:bg-[#004a82] text-white text-sm font-semibold" data-testid="contact-submit-btn">
                {loading ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Sending...</> : <><Send size={14} className="mr-1.5" /> {language === 'ta' ? 'அனுப்பு' : 'Send Message'}</>}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
